// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// The overlay implementation used by every modal and drill-down on the site.
//
// THE DISMISSAL CONTRACT. Every overlay ships all four exits, and they are not
// alternatives: (1) a ✕ of at least 44×44, inside the panel; (2) a tap anywhere
// outside it, wired on `pointerdown` so mouse, touch and pen are all covered by
// one handler; (3) Escape from anywhere; (4) a scrim that is genuinely
// reachable — fixed, inset:0, below the panel, above the page, and never
// pointer-events:none.
//
// The panel is DETACHED from the DOM when closed rather than parked off-canvas.
// A closed drawer left at translateX(100%) is still a real box on iOS Safari and
// silently makes the whole page scroll sideways — and `overflow-x: clip` on the
// body neither fixes it nor lets scrollWidth detect it.

import type { Site } from '../types';
import { PRODUCT_NAMES, cents, deltaLabel, esc, shortDate } from '../format';

let host: HTMLElement | null = null;
let scrim: HTMLElement | null = null;
let onKey: ((e: KeyboardEvent) => void) | null = null;
let lastFocus: HTMLElement | null = null;

export function closeOverlay(): void {
  if (onKey) { document.removeEventListener('keydown', onKey); onKey = null; }
  if (host) { host.remove(); host = null; }
  if (scrim) { scrim.remove(); scrim = null; }
  document.body.style.overflow = '';
  if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
  lastFocus = null;
}

export function isOverlayOpen(): boolean {
  return host !== null;
}

/**
 * Open an overlay. `kind` selects the presentation; the dismissal contract is
 * identical either way.
 */
export function openOverlay(kind: 'modal' | 'drawer', html: string, label: string): HTMLElement {
  closeOverlay();
  lastFocus = document.activeElement as HTMLElement | null;

  scrim = document.createElement('div');
  scrim.className = 'scrim';
  // pointerdown, not click: it fires for touch and pen as well as mouse, and it
  // fires in places a synthetic click would not. Using click AND touchstart on
  // the same control double-fires, which reads as "the close button does
  // nothing" because it closes and instantly reopens.
  scrim.addEventListener('pointerdown', (e) => { e.preventDefault(); closeOverlay(); });
  document.body.appendChild(scrim);

  host = document.createElement('div');
  host.className = kind;
  host.setAttribute('role', 'dialog');
  host.setAttribute('aria-modal', 'true');
  host.setAttribute('aria-label', label);
  host.style.position = 'fixed';
  host.innerHTML = `<button type="button" class="modal-close" aria-label="Close">✕</button>${html}`;
  host.querySelector('.modal-close')!.addEventListener('pointerdown', (e) => { e.preventDefault(); closeOverlay(); });
  // Clicks inside the panel must not reach a document-level closer.
  host.addEventListener('pointerdown', (e) => e.stopPropagation());
  document.body.appendChild(host);

  onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); closeOverlay(); } };
  document.addEventListener('keydown', onKey);
  document.body.style.overflow = 'hidden';
  (host.querySelector('.modal-close') as HTMLElement).focus();
  return host;
}

/** Station drill-down, reachable from the table, the map and the spread view. */
export function openStation(site: Site): void {
  const products = Object.entries(site.p).sort((a, b) => a[1] - b[1]);
  const rows = products.map(([code, price]) => {
    const t = site.t?.[code];
    return `<tr>
      <td>${esc(PRODUCT_NAMES[code] ?? code)}</td>
      <td class="n">${cents(price)}c</td>
      <td class="n">${t != null ? `${cents(t)}c <small>${deltaLabel(t - price)}</small>` : '—'}</td>
    </tr>`;
  }).join('');
  openOverlay('drawer', `
    <h2>${esc(site.n)}</h2>
    <p class="sub">${esc(site.b || 'Independent')} · ${esc(site.s)} ${esc(site.pc)} · ${esc(site.r)} (${esc(site.st)})</p>
    <div class="table-scroll" style="margin-top:var(--space-lg)">
      <table>
        <thead><tr><th>Fuel</th><th class="n">Latest</th><th class="n">Tomorrow</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
    <p class="brand-sub" style="margin-top:var(--space-md)">
      Last reported ${esc(shortDate(site.d))}.
      ${site.st === 'WA'
        ? 'Western Australian prices are notified the day before and held for 24 hours from 6am.'
        : 'Queensland stations report only when a price changes, so this is the last price this station notified.'}
    </p>
    ${site.lat != null && site.lon != null
      ? `<p class="brand-sub" style="margin-top:var(--space-sm)">Location ${site.lat.toFixed(4)}, ${site.lon.toFixed(4)}</p>`
      : '<p class="brand-sub" style="margin-top:var(--space-sm)">No published coordinates for this station.</p>'}
  `, `${site.n}, ${site.s}`);
}
